import { NavbarHeightContext } from '@contexts/navbar-height'
import { useMatchMedia } from '@hooks/use-match-media'
import { ContactModal } from '@ui/modal/contact/contact-modal'
import { size } from '@utils/breakpoints'
import { graphql, useStaticQuery } from 'gatsby'
import React, {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

import { useIsIframe } from '../../../../plugins/gatsby-plugin-espace-client'
import CardAdvantage from './card/card-advantage'
import CardHeader from './card/card-header'
import CardImage from './card/card-image'
import Carousel from './carousel'
import {
  CARD_FIELDS,
  FOSFO,
  FOSFO_SHARED,
  getWorldEliteShared,
  GOLD,
  GOLD_SHARED,
  WORLD_ELITE,
} from './fortuneo-cards-data'

type ModalContent =
  | {
      title: string
      staticContent: ReactNode
    }
  | undefined

const Comparator: FC<{ isShared?: boolean }> = ({ isShared = false }) => {
  const cardsData = [FOSFO, GOLD, WORLD_ELITE]
  const cardsSharedData = [FOSFO_SHARED, GOLD_SHARED, getWorldEliteShared()]
  const advantagesKeys = Object.keys(cardsData[0].advantages)
  const data = isShared ? cardsSharedData : cardsData
  const headers = data.map((card) => card.general)
  const { height: navbarHeight } = useContext(NavbarHeightContext)
  const [modalContent, setModalContent] = useState<ModalContent>(undefined)
  const isMdDown = useMatchMedia(`(max-width: ${size.laptop})`)
  const isIframe = useIsIframe()
  const showDesktop = isIframe || !isMdDown

  const headerAssets = useStaticQuery<Queries.CardComparatorAssetsQuery>(
    graphql`
      fragment CardComparatorAsset on ContentfulAsset {
        description
        gatsbyImageData(formats: [AVIF, WEBP, AUTO])
      }

      query CardComparatorAssets {
        fosfo: contentfulAsset(
          contentful_id: { eq: "bUN4w0xXdUpvVf3QqL66f" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...CardComparatorAsset
        }
        gold: contentfulAsset(
          contentful_id: { eq: "4TIkeHuEoqyMDGF5YY5zSl" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...CardComparatorAsset
        }
        worldElite: contentfulAsset(
          contentful_id: { eq: "1AgJsCkGl5mJx4YOBZQJRC" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...CardComparatorAsset
        }
      }
    `
  )

  const open = useCallback((title: string, content: ReactNode) => {
    setModalContent({
      title,
      staticContent: content,
    })
  }, [])

  const close = useCallback(() => {
    setModalContent(null)
  }, [])

  return (
    <>
      <div className="cards-comparator container max-w-none px-4 laptopL:max-w-screen-laptopL laptopL:px-[132px]">
        <Carousel rows={advantagesKeys.length + 2}>
          {headers.map((headerProps, index) => (
            <Carousel.Slide key={index} className="flex-[1_0_33%]">
              <CardImage
                {...headerProps}
                asset={
                  headerAssets[
                    headerProps.assetName as keyof Queries.CardComparatorAssetsQuery
                  ]
                }
              />
            </Carousel.Slide>
          ))}
          {headers.map((headerProps, index) => (
            <Carousel.Slide
              key={index}
              className="!sticky z-10 flex-[1_0_33%]"
              style={{ top: navbarHeight }}
            >
              <CardHeader
                {...headerProps}
                asset={
                  headerAssets[
                    headerProps.assetName as keyof Queries.CardComparatorAssetsQuery
                  ]
                }
              />
            </Carousel.Slide>
          ))}
          {advantagesKeys.map((advantageKey) =>
            data.map((card) => {
              const advantage = card[CARD_FIELDS.BODY][advantageKey]
              return (
                <Carousel.Slide
                  key={card.general.assetName}
                  className="flex-[1_0_33%]"
                >
                  <CardAdvantage advantage={advantage} openModal={open} />
                </Carousel.Slide>
              )
            })
          )}
        </Carousel>
      </div>
      <ContactModal
        {...modalContent}
        fullScreen={!showDesktop}
        open={Boolean(modalContent)}
        onClose={close}
      />
    </>
  )
}

export default Comparator
